// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback } from 'react';
import type { RejectionReasonOption, ProviderRejectionRequest, RejectionReasonCode } from '../../types/providerAccess';

interface RejectionPanelProps {
  rejectionReasons: RejectionReasonOption[];
  submitting: boolean;
  onReject: (request: ProviderRejectionRequest) => Promise<boolean>;
  /** Override the submit button label. Defaults to "Reject Letter Of Authority". */
  submitLabel?: string;
}

export function RejectionPanel({ rejectionReasons, submitting, onReject, submitLabel }: RejectionPanelProps) {
  const [selectedCode, setSelectedCode] = useState<RejectionReasonCode | ''>('');
  const [freeText, setFreeText] = useState('');

  const isOther = selectedCode === 'OTHER';
  const canSubmit = selectedCode !== '' && (!isOther || freeText.trim() !== '');

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !selectedCode) return;

    const request: ProviderRejectionRequest = {
      rejectionReasonCode: selectedCode,
    };
    if (freeText.trim()) {
      request.rejectionReason = freeText.trim();
    }
    await onReject(request);
  }, [canSubmit, selectedCode, freeText, onReject]);

  return (
    <div className="flex flex-col mt-md">
      <div className="flex flex-col gap-sm">
        <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
          Please select a reason for rejection <span className="text-[#E7000B]">*</span>
        </div>
        <div className="relative">
          <select
            value={selectedCode}
            onChange={(e) => {
              setSelectedCode(e.target.value as RejectionReasonCode);
            }}
            className={`w-full h-[48px] px-md pr-xxl rounded-lg border border-grey-04 bg-white ${selectedCode ? 'text-[#333]' : 'text-grey-04'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`}
          >
            <option value="" disabled className="text-[#B6B6B6]">Select a reason</option>
            {rejectionReasons
              .filter((r) => r.displayText !== 'Other reason for rejection')
              .map((r) => (
                <option key={r.code} value={r.code}>{r.displayText}</option>
              ))}
          </select>
          <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 7.2C5.533 7.2 5.067 7.02 4.713 6.666L0.367 2.32C0.173 2.126 0.173 1.806 0.367 1.613C0.56 1.42 0.88 1.42 1.073 1.613L5.42 5.96C5.74 6.28 6.26 6.28 6.58 5.96L10.927 1.613C11.12 1.42 11.44 1.42 11.633 1.613C11.827 1.806 11.827 2.126 11.633 2.32L7.287 6.666C6.933 7.02 6.467 7.2 6 7.2Z" fill="#141414"/>
          </svg>
        </div>
      </div>

      <div className="flex flex-col gap-sm mt-md">
        <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
          Please provide additional details.{isOther && <span className="text-[#E7000B]"> *</span>}
        </div>
        <textarea
          placeholder="Please provide additional details."
          maxLength={2000}
          value={freeText}
          onChange={(e) => {
            setFreeText(e.target.value);
          }}
          className="w-full min-h-[120px] px-md py-[12px] rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary resize-y"
        />
      </div>

      <div className="flex justify-end mt-lg">
        <button
          type="button"
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
          className="h-[48px] px-xl rounded-button bg-error-text border-none cursor-pointer transition-colors hover:bg-[#7A0510] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="text-white font-sans text-body-02 font-medium leading-normal not-italic capitalize">
            {submitting ? 'Rejecting...' : (submitLabel ?? 'Reject Letter Of Authority')}
          </div>
        </button>
      </div>
    </div>
  );
}
